import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Search, Milk } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { AnnouncementBanner } from "@/components/Announcements";
import { toast } from "sonner";

export default function Shop() {
  const { brand } = useOutletContext();
  const dark = brand.key === "blackpekoe";
  const fg = dark ? "#F5F2EB" : "#2D2622";
  const muted = dark ? "rgba(245,242,235,0.7)" : "#5C5753";
  const subtle = dark ? "rgba(245,242,235,0.55)" : "#8C8581";
  const cardBg = dark ? "rgba(255,255,255,0.04)" : "white";
  const cardBorder = dark ? "rgba(255,255,255,0.08)" : "rgba(45,38,34,0.08)";
  const inputBg = dark ? "rgba(255,255,255,0.05)" : "white";

  const [products, setProducts] = useState([]);
  const [milkProducts, setMilkProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const { add, ensureBrand } = useCart();
  const { user } = useAuth();
  const isMilkPartner = brand.key === "teaboy" && user?.is_milk_partner;

  useEffect(() => {
    ensureBrand(brand.key);
    api.get("/products/categories", { params: { brand: brand.key } }).then(({ data }) => setCategories(data.categories || []));
    // Load Milk top-section only for eligible TEABOY milk-partners
    if (isMilkPartner) {
      api.get("/products/milk", { params: { brand: brand.key } })
        .then(({ data }) => setMilkProducts(data))
        .catch(() => setMilkProducts([]));
    } else {
      setMilkProducts([]);
    }
  }, [brand.key, isMilkPartner]); // eslint-disable-line

  useEffect(() => {
    setLoading(true);
    const params = { brand: brand.key };
    if (category !== "all") params.category = category;
    if (q) params.q = q;
    const t = setTimeout(() => {
      api.get("/products", { params }).then(({ data }) => setProducts(data)).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, q, brand.key]);

  const handleAdd = (p) => {
    add(p, 1);
    toast.success(`${p.name} added to cart`);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10" style={{ color: fg }}>
      <div className="mb-8">
        <p className="overline" style={{ color: subtle }}>{brand.name} catalog</p>
        <h1 className="font-heading font-bold text-3xl sm:text-4xl mt-2">{dark ? "Live brew. 100% natural." : "Chai for everyone."}</h1>
      </div>

      <AnnouncementBanner brand={brand} />

      {isMilkPartner && milkProducts.length > 0 && (
        <section data-testid="milk-top-section" className="mb-10 rounded-2xl p-6 lg:p-8"
          style={{ background: "linear-gradient(135deg, #FFF8EC 0%, #FFE9D6 100%)", border: `1px solid ${brand.accent}55` }}>
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ background: brand.primary, color: "white" }}>
                <Milk className="w-6 h-6" />
              </div>
              <div>
                <p className="overline" style={{ color: brand.primary }}>Daily essentials · Chennai exclusive</p>
                <h2 className="font-heading font-bold text-2xl text-[#2D2622]">Milk &amp; dairy</h2>
              </div>
            </div>
            {user?.milk_partner_code && (
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-widest text-[#8C8581]">Your milk-partner code</p>
                <p data-testid="milk-partner-code" className="font-mono font-bold text-[#C85A32] text-lg">{user.milk_partner_code}</p>
              </div>
            )}
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {milkProducts.map((p) => {
              const out = (p.stock ?? 0) <= 0;
              return (
                <div key={p.id} data-testid={`milk-product-${p.id}`}
                  className="rounded-xl overflow-hidden bg-white border border-[#E69C24]/40 hover:-translate-y-0.5 transition-transform">
                  <div className="h-32 overflow-hidden bg-[#F5F2EB]">
                    <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-3">
                    <h3 className="font-heading font-semibold text-[#2D2622] text-sm line-clamp-1">{p.name}</h3>
                    <div className="mt-2 flex items-end justify-between">
                      <div>
                        <p className="font-heading font-bold text-[#2D2622]">₹{p.price.toFixed(2)}</p>
                        <p className="text-[10px] text-[#8C8581]">per {p.unit}</p>
                      </div>
                      <Button size="sm" disabled={out} data-testid={`add-milk-${p.id}`} onClick={() => handleAdd(p)} className="bg-[#C85A32] hover:bg-[#A64A28] text-white h-8 px-2">
                        <Plus className="w-3 h-3 mr-0.5" /> {out ? "Out" : "Add"}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: subtle }} />
          <Input
            data-testid="product-search"
            placeholder="Search…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-10"
            style={{ background: inputBg, color: fg, borderColor: cardBorder }}
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setCategory("all")} data-testid="cat-all"
            className="px-4 py-2 rounded-full text-sm font-semibold transition"
            style={{ background: category === "all" ? brand.primary : cardBg, color: category === "all" ? "white" : fg, border: `1px solid ${cardBorder}` }}>
            All
          </button>
          {categories.map((c) => (
            <button key={c} data-testid={`cat-${c}`} onClick={() => setCategory(c)}
              className="px-4 py-2 rounded-full text-sm font-semibold transition"
              style={{ background: category === c ? brand.primary : cardBg, color: category === c ? "white" : fg, border: `1px solid ${cardBorder}` }}>
              {c}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="rounded-xl animate-pulse" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
              <div className="h-48" style={{ background: dark ? "rgba(255,255,255,0.05)" : "#F5F2EB" }} />
              <div className="p-4 space-y-2">
                <div className="h-4 rounded w-3/4" style={{ background: dark ? "rgba(255,255,255,0.05)" : "#F5F2EB" }} />
                <div className="h-3 rounded w-1/2" style={{ background: dark ? "rgba(255,255,255,0.05)" : "#F5F2EB" }} />
              </div>
            </div>
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-20" style={{ color: subtle }} data-testid="no-products">No products found.</div>
      ) : (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {products.map((p, i) => {
            const out = (p.stock ?? 0) <= 0;
            const low = !out && (p.stock ?? 0) <= 10;
            return (
              <div key={p.id} data-testid={`product-card-${p.id}`}
                className="rounded-xl overflow-hidden transition-all fade-up hover:-translate-y-0.5"
                style={{ background: cardBg, border: `1px solid ${cardBorder}`, animationDelay: `${i * 40}ms` }}>
                <div className="h-48 overflow-hidden relative" style={{ background: dark ? "rgba(255,255,255,0.04)" : "#F5F2EB" }}>
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                  {out && <span data-testid={`out-of-stock-${p.id}`} className="absolute top-2 left-2 bg-[#D94532] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded">Out of stock</span>}
                  {low && <span className="absolute top-2 left-2 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded" style={{ background: brand.accent }}>Only {p.stock} left</span>}
                </div>
                <div className="p-4">
                  <p className="text-xs uppercase tracking-wider font-semibold" style={{ color: subtle }}>{p.category}</p>
                  <h3 className="font-heading font-semibold mt-1 line-clamp-1">{p.name}</h3>
                  <p className="text-sm mt-1 line-clamp-2 leading-snug" style={{ color: muted }}>{p.description}</p>
                  <div className="mt-4 flex items-end justify-between">
                    <div>
                      <p className="font-heading font-bold text-xl">₹{p.price.toFixed(2)}</p>
                      <p className="text-xs" style={{ color: subtle }}>per {p.unit}</p>
                    </div>
                    <Button size="sm" disabled={out} data-testid={`add-cart-${p.id}`} onClick={() => handleAdd(p)} className="text-white disabled:opacity-40" style={{ background: brand.primary }}>
                      <Plus className="w-4 h-4 mr-1" /> {out ? "Sold out" : "Add"}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
